import {TimelineLite} from "gsap";
// @ts-ignore
import {Howler} from "howler";
import * as HLF from "html-living-framework";
import * as springroll from "springroll";
import {GoogleAnalytics} from "springroll-google-analytics-plugin";
import {Assets} from "./data/Assets";
import {CHOOSE_STORY_NAME} from "./data/IChooseStoryData";
import {DefaultCaptionRenderer} from "./gameobjects/DefaultCaptionRenderer";
import {AnalyticsManager} from "./managers/AnalyticsManager";
import {AudioChannel} from "./managers/AudioChannel";
import {CaptionManager} from "./managers/CaptionManager";
import {VisibilityManager} from "./managers/VisibilityManager";
import {ChooseStory} from "./states/ChooseStory";
import {LoadScreen} from "./states/LoadScreen";
import {SceneCreator} from "./states/SceneCreator";
import {StartScreen} from "./states/StartScreen";
import {StoryCreator} from "./states/StoryCreator";
import {StoryLoadScreen} from "./states/StoryLoadScreen";

export class Application extends HLF.Application {

    static get instance() {
        if (HLF.Application._instance === undefined) {
            HLF.Application._instance = new Application();
        }
        return HLF.Application._instance as Application;
    }

    public get requiredAssets(): HLF.AssetMapData[] {
        return [];
    }

    public get springroll() {
        return this._springroll;
    }

    public resizeDisabled = false;

    private _springroll!: springroll.Application;
    private _paused: boolean = false;

    constructor() {
        super({
            transparent: true,
            antialias: true,
            preserveDrawingBuffer: (
                PIXI.utils.isMobile.android.device ||
                PIXI.utils.isMobile.amazon.device
            ) ? true : undefined,
        });
        this._audioManager.debug = false;
        this._stateManager.debug = false;
        this._loadManager.debug = false;
        this._popupManager.debug = false;

        PIXI.settings.FILTER_RESOLUTION = this.renderer.resolution;

        (window as any).gameDebug = {
            pause: () => this.onPause(true),
            resume: () => this.onPause(false),
        };
    }

    public createAssetMap() {
        super.createAssetMap();

        HLF.AssetMap.addAssetGroup(HLF.SplashScreen.NAME, Assets.boot);
        HLF.AssetMap.addAssetGroup(CHOOSE_STORY_NAME, [
            ...Assets.common,
            ...Assets.storySelect,
            ...Assets.bookColorRamps,
            Assets.audio.bgmMenu,
            Assets.audio.buttonPress,
            Assets.audio.bookOpen,
            Assets.audio.bookSlide,
            ...Assets.audio.libraryIntroVO,
            ...Assets.audio.menuIdle,
            ...Assets.storyLoader,
        ]);
        HLF.AssetMap.addAssetGroup(SceneCreator.NAME, [
            ...Assets.common,
            ...Assets.sceneCreator,
            Assets.paperBackground,
        ]);
    }

    public createSplashScreen() {
        return super.createSplashScreen();
    }

    public registerStates() {
        super.registerStates();

        this._stateManager.registerState(CHOOSE_STORY_NAME, () => new ChooseStory());
        this._stateManager.registerState(StoryCreator.NAME, () => new StoryCreator());
        this._stateManager.registerState(SceneCreator.NAME, () => new SceneCreator());
    }

    public registerLoadingScreens() {
        super.registerLoadingScreens();

        this._loadManager.registerLoadScreen(StartScreen.NAME, () => new StartScreen());
        this._loadManager.registerLoadScreen(LoadScreen.NAME, () => new LoadScreen());
        this._loadManager.registerLoadScreen(StoryLoadScreen.NAME, () => new StoryLoadScreen());
    }

    public registerPopups() {
        super.registerPopups();
    }

    public update() {
        super.update();
    }

    public init() {
        super.init();

        this.initSpringroll();

        // this._hitAreaRenderer.active = true;
        // this._hitAreaRenderer.interval = 1;

        // if (this.saveManager.readBool("story-creator-mute")) {
        //     this.audio.masterVolume = 0;
        // }

        this.resizer.sizeMax = new PIXI.Point(1664, 768);
        this.initVisibilityManager();

        // HTML5 audio on iOS
        if (PIXI.utils.isMobile.apple.device) {
            Howler.usingWebAudio = false;
        }

        this.initExitGameHandler();
    }

    protected onResize(pDelay?: number) {
        if (!this.resizeDisabled) {
            super.onResize(pDelay);
        }
        new TimelineLite().call(() => {
            CaptionManager.instance.onResize(this.resizer.getSize());
        }, undefined, this, pDelay);
    }

    protected onLoadRequiredAssetsComplete() {
        super.onLoadRequiredAssetsComplete();

        const captionRenderer = this.stage.addChild(new DefaultCaptionRenderer());
        CaptionManager.instance.setRenderer(captionRenderer);
        CaptionManager.instance.onResize(this._size);

        PubSub.publishSync(
            HLF.STATE_LOAD_STATE,
            new HLF.StateToken(CHOOSE_STORY_NAME, StartScreen.NAME,
                HLF.TransitionStep.ShowLoadScreen,
                ...HLF.StateToken.TRANSITION_FIRST_VIEW),
        );
        PubSub.publish(HLF.PUSH_KEYBOARD_LAYER, undefined);
    }

    private initVisibilityManager() {
        let wasPaused = false;
        VisibilityManager.instance.onChange((visisble) => {
            if (!visisble) {
                wasPaused = this._paused;
                this.onPause(true);
            } else if (visisble && !wasPaused) {
                this.onPause(false);
            }
        });
    }

    private initSpringroll(): void {
        this._springroll = new springroll.Application({
            features: {
                captions: true,
                sound: true,
                vo: true,
                music: true,
                sfx: false,
            },
        });

        springroll.Application.uses(new GoogleAnalytics());

        this._springroll.state.ready.subscribe(() => {
            this.onSpringrollInitialized();
        });

        this._springroll.state.pause.subscribe((isPaused: boolean) => {
            this.onPause(isPaused);
        });

        this._springroll.state.playOptions.subscribe((playOptions: object) => {
            console.log("New playOptions value set to", playOptions);
        });

        this._springroll.state.captionsMuted.subscribe((result: boolean) => {
            this.onCaptionsMuted(result);
        });

        this._springroll.state.soundVolume.subscribe((result: number) => {
            this.onSoundVolume(result);
        });

        this._springroll.state.musicVolume.subscribe((result: number) => {
            this.onMusicVolume(result);
            this.onSFXVolume(result);
        });

        this._springroll.state.voVolume.subscribe((result: number) => {
            this.onVOVolume(result);
        });
    }

    private onSpringrollInitialized(): void {
        // override
    }

    // Springroll message listeners
    private onCaptionsMuted(pMuted: boolean): void {
        CaptionManager.instance.active = !pMuted;
    }

    private onSoundVolume(pResults: number): void {
        AudioChannel.mute = pResults === 0;
        this.audio.masterVolume = pResults;
    }

    private onVOVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.VO, pResults);
    }

    private onMusicVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.BGM, pResults);
    }

    private onSFXVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.SFX, pResults);
    }

    private onPause(pIsPaused: boolean): void {
        const wasPaused = this._paused;
        if (pIsPaused && !wasPaused) {
            AudioChannel.mute = true;
            PubSub.publishSync(HLF.GAME_PAUSED, undefined);
            // PubSub.publishSync(HLF.SHOW_POPUP, new HLF.PopupToken("pause", undefined, "static", false));
        } else if (wasPaused && !pIsPaused) {
            AudioChannel.mute = false;
            // PubSub.publishSync(HLF.HIDE_POPUP, "pause");
            PubSub.publishSync(HLF.GAME_UNPAUSED, undefined);
        }
        this._paused = pIsPaused;
    }

    private initExitGameHandler() {
        window.addEventListener("beforeunload", (event) => {
            AnalyticsManager.event("Story Creator", "endGame");
        });
    }
}
